import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from 'lucide-react';
import { theme } from '../config/theme';

const PrivacyPolicy: React.FC = () => {
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Navigation */}
        <Link 
          to="/home"
          className="inline-flex items-center space-x-2 text-sm mb-8 transition-colors hover:text-blue-600"
          style={{ color: theme.colors.primary[600] }}
        >
          <ArrowLeftIcon className="h-4 w-4" />
          <span>Back to Home</span>
        </Link>

        {/* Privacy Policy Content */}
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-bold mb-2" style={{ color: theme.colors.primary[900] }}>
            Privacy Policy
          </h1>
          <h2 className="text-xl mb-8" style={{ color: theme.colors.primary[700] }}>
            Bounce Dads ATL
          </h2>
          
          <p className="text-sm text-gray-600 mb-8">
            Effective Date: November 12, 2025
          </p>

          <div className="prose prose-sm max-w-none" style={{ color: theme.colors.text.primary }}>
            <p className="mb-6">
              Bounce Dads ATL ("we," "us," or "our", LLH Party Rentals) values your privacy and is committed to protecting your personal information. This Privacy Policy describes how we collect, use, and safeguard your information when you use our website, online booking system, or services.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4" style={{ color: theme.colors.primary[800] }}>
              1. Information We Collect
            </h2>
            
            <p className="mb-4">
              When you use our online booking system, contact us, or rent our inflatable equipment, we may collect the following information:
            </p>

            <h3 className="text-lg font-semibold mt-6 mb-3" style={{ color: theme.colors.primary[700] }}>
              Personal Information
            </h3>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>Full name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Billing and event address</li>
              <li>Event details (date, time, location, rental items)</li>
            </ul>

            <h3 className="text-lg font-semibold mt-6 mb-3" style={{ color: theme.colors.primary[700] }}>
              Payment Information
            </h3>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>Payments are processed securely through third-party payment processors.</li>
              <li>Bounce Dads ATL does not store full credit or debit card numbers.</li>
            </ul>

            <h3 className="text-lg font-semibold mt-6 mb-3" style={{ color: theme.colors.primary[700] }}>
              Automatically Collected Information
            </h3>
            <p className="mb-4">
              When visiting our website, we may automatically collect:
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>IP address</li>
              <li>Browser type and device information</li>
              <li>Pages visited and booking activity</li>
              <li>Date and time of access</li>
            </ul>
            <p className="mb-6">
              This data helps us improve site functionality and booking efficiency.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4" style={{ color: theme.colors.primary[800] }}>
              2. How We Use Your Information
            </h2>
            
            <p className="mb-4">
              We use your information to:
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>Process and manage online bookings</li>
              <li>Confirm reservations, deliveries, and pickups</li>
              <li>Communicate important service updates</li>
              <li>Process payments and deposits</li>
              <li>Respond to customer inquiries</li>
              <li>Improve our website, booking system, and services</li>
              <li>Send service-related or promotional messages (you may opt out at any time)</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4" style={{ color: theme.colors.primary[800] }}>
              3. Legal Basis for Processing (Georgia Compliance)
            </h2>
            
            <p className="mb-4">
              We collect and use personal information only when it is:
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>Necessary to fulfill a rental contract</li>
              <li>Required to comply with legal obligations</li>
              <li>Based on your consent (such as opting into emails or texts)</li>
              <li>In our legitimate business interest to operate and improve our services</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4" style={{ color: theme.colors.primary[800] }}>
              4. Sharing Your Information
            </h2>
            
            <p className="mb-4">
              We do not sell your personal information.
            </p>
            <p className="mb-4">
              We may share information only with:
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>Online booking and scheduling software providers</li>
              <li>Payment processors</li>
              <li>Website hosting and analytics providers</li>
              <li>Legal authorities when required by Georgia or federal law</li>
            </ul>
            <p className="mb-6">
              All third parties are required to maintain confidentiality and data security.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4" style={{ color: theme.colors.primary[800] }}>
              5. Data Security
            </h2>
            
            <p className="mb-4">
              Bounce Dads ATL uses reasonable administrative, technical, and physical safeguards to protect your personal information, including secure booking platforms and encrypted payment processing.
            </p>
            <p className="mb-6">
              In accordance with Georgia's Personal Identity Protection Act (PIPA), if a data breach occurs involving your personal information, we will notify affected individuals as required by law.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4" style={{ color: theme.colors.primary[800] }}>
              6. Cookies & Online Tracking
            </h2>
            
            <p className="mb-4">
              Our website may use cookies or similar tracking technologies to:
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>Improve user experience</li>
              <li>Remember booking preferences</li>
              <li>Analyze website traffic</li>
            </ul>
            <p className="mb-6">
              You may disable cookies through your browser settings; however, some booking features may not function properly.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4" style={{ color: theme.colors.primary[800] }}>
              7. Your Privacy Rights
            </h2>
            
            <p className="mb-4">
              You have the right to:
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>Request access to the personal information we have about you</li>
              <li>Request correction or deletion of your information</li>
              <li>Opt out of marketing communications at any time</li>
            </ul>
            <p className="mb-6">
              Requests can be made by contacting us using the information below.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4" style={{ color: theme.colors.primary[800] }}>
              8. Children's Privacy
            </h2>
            
            <p className="mb-6">
              Bounce Dads ATL does not knowingly collect personal information from children under the age of 13. Our services are intended for adults booking event rentals.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4" style={{ color: theme.colors.primary[800] }}>
              9. Retention of Information
            </h2>
            
            <p className="mb-4">
              We retain personal information only as long as necessary to:
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-1">
              <li>Fulfill bookings and business records</li>
              <li>Comply with legal and tax obligations</li>
              <li>Resolve disputes and enforce agreements</li>
            </ul>

            <h2 className="text-xl font-semibold mt-8 mb-4" style={{ color: theme.colors.primary[800] }}>
              10. Changes to This Privacy Policy
            </h2>
            
            <p className="mb-6">
              We may update this Privacy Policy periodically. Any changes will be posted on this page with an updated effective date.
            </p>

            <h2 className="text-xl font-semibold mt-8 mb-4" style={{ color: theme.colors.primary[800] }}>
              11. Contact Information
            </h2>
            
            <p className="mb-4">
              If you have questions about this Privacy Policy or how your data is handled, please contact us:
            </p>
            <div className="pl-6 mb-6 space-y-2">
              <p><strong>Bounce Dads ATL</strong></p>
              <p>Email: support@bouncedadsatl.com</p>
              <p>Phone: (404) 955-8126</p>
              <p>Location: Atlanta, Georgia</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
